import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Calendar, AlertCircle, Loader2, RefreshCw, Plus, Filter } from "lucide-react";
import AppointmentCard from "@/components/AppointmentCard";
import ProviderAppointmentCard, { providerColor } from "@/components/ProviderAppointmentCard";
import NextUpBanner from "@/components/NextUpBanner";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AppointmentActions from "@/components/AppointmentActions";
import IntakeQReconnectDialog from "@/components/IntakeQReconnectDialog";
import BookAppointmentDialog from "@/components/BookAppointmentDialog";

export default function Appointments() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isProvider = user?.role === "admin";
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState(user?.role === "admin" ? "today" : "upcoming");
  const [selectedApt, setSelectedApt] = useState(null);
  const [reconnectDialogOpen, setReconnectDialogOpen] = useState(false);
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("local");
  const [syncing, setSyncing] = useState(false);
  const [providerFilter, setProviderFilter] = useState("all");

  const fetchAppointments = useCallback(async (showSync = false) => {
    if (showSync) setSyncing(true);
    try {
      if (isProvider) {
        const { data } = await API.get("/intakeq/provider-appointments");
        setAppointments(data?.appointments || []);
        setSource(data?.source || "none");
        if (data?.source === "none" && data?.error) {
          toast({
            title: "IntakeQ sync failed",
            description: "Couldn't reach IntakeQ. Showing cached data if available.",
            variant: "destructive",
          });
        }
      } else {
        const { data: intakeqData } = await API.get("/intakeq/appointments");
        if (intakeqData?.appointments?.length > 0) {
          setAppointments(intakeqData.appointments);
          setSource("intakeq");
        } else {
          const { data: localData } = await API.get("/appointments");
          setAppointments(localData || []);
          setSource("local");
        }
      }
    } catch {
      if (!isProvider) {
        try {
          const { data: localData } = await API.get("/appointments");
          setAppointments(localData || []);
          setSource("local");
        } catch { }
      }
    }
    setLoading(false);
    setSyncing(false);
  }, [isProvider, toast]);

  useEffect(() => { if (user) fetchAppointments(); }, [user, fetchAppointments]);

  const isUpcoming = (apt) => {
    const status = (apt.status || "").toLowerCase();
    return ["scheduled", "confirmed"].includes(status);
  };

  const isToday = (apt) => {
    if (!isUpcoming(apt)) return false;
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
    // Prefer start_iso when present (provider endpoint); fall back to parsing apt.date
    if (apt.start_iso) {
      const dt = new Date(apt.start_iso);
      return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
    }
    if (apt.date) {
      const dt = new Date(apt.date);
      if (!isNaN(dt.getTime())) return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
    }
    return false;
  };

  const upcoming = appointments.filter(isUpcoming);
  const today = appointments.filter(isToday);
  const past = appointments.filter(a => !isUpcoming(a));

  // Provider filter list derived from appointments themselves
  const providerOptions = useMemo(() => {
    if (!isProvider) return [];
    const seen = new Map();
    appointments.forEach(a => {
      const key = a.practitioner_id || a.practitioner_name;
      if (key && !seen.has(key)) {
        seen.set(key, { key, name: a.practitioner_name, count: 0 });
      }
      if (key) seen.get(key).count += 1;
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [appointments, isProvider]);

  const filteredUpcoming = isProvider && providerFilter !== "all"
    ? upcoming.filter(a => (a.practitioner_id || a.practitioner_name) === providerFilter)
    : upcoming;
  const filteredToday = isProvider && providerFilter !== "all"
    ? today.filter(a => (a.practitioner_id || a.practitioner_name) === providerFilter)
    : today;
  const filteredPast = isProvider && providerFilter !== "all"
    ? past.filter(a => (a.practitioner_id || a.practitioner_name) === providerFilter)
    : past;

  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isProvider ? "max-w-3xl" : "max-w-lg"} mx-auto px-4 py-6 animate-fade-in`} data-testid="appointments-page">
      <div className="flex items-start justify-between">
        <PageHeader
          title="Appointments"
          subtitle={isProvider ? "All upcoming appointments across the practice" : "Manage your appointments"}
        />
        <div className="flex gap-2 mt-2">
          {!isProvider && (
            <Button size="sm" onClick={() => setBookDialogOpen(true)} className="gap-1.5 rounded-full" data-testid="book-appointment-button">
              <Plus className="w-3.5 h-3.5" /> Book
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => fetchAppointments(true)} disabled={syncing} className="gap-1.5" data-testid="sync-appointments-button">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {source === "intakeq" && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 mb-4 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
          <p className="text-xs text-green-800 font-medium">Synced from IntakeQ</p>
        </div>
      )}

      {isProvider && providerOptions.length > 1 && (
        <div className="mb-4 flex items-center gap-2" data-testid="provider-filter-bar">
          <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="h-9" data-testid="provider-filter-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All providers ({appointments.length})</SelectItem>
              {providerOptions.map((p) => {
                const color = providerColor(p.key);
                return (
                  <SelectItem key={p.key} value={p.key} data-testid={`provider-filter-option-${p.key}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${color.dot}`} />
                      {p.name} ({p.count})
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full mb-4">
          {isProvider && (
            <TabsTrigger value="today" className="flex-1" data-testid="tab-today">Today ({filteredToday.length})</TabsTrigger>
          )}
          <TabsTrigger value="upcoming" className="flex-1" data-testid="tab-upcoming">Upcoming ({filteredUpcoming.length})</TabsTrigger>
          <TabsTrigger value="past" className="flex-1" data-testid="tab-past">Past ({filteredPast.length})</TabsTrigger>
        </TabsList>
        {isProvider && (
          <TabsContent value="today" className="space-y-3">
            {filteredToday.length === 0 ? (
              <EmptyState icon={Calendar} title="Nothing scheduled for today" description="Enjoy the breathing room." />
            ) : (
              <>
                <NextUpBanner appointments={filteredToday} />
                {filteredToday.map((apt) => <ProviderAppointmentCard key={apt.id || apt.intakeq_id} appointment={apt} />)}
              </>
            )}
          </TabsContent>
        )}
        <TabsContent value="upcoming" className="space-y-3">
          {filteredUpcoming.length === 0 ? (
            <div className="space-y-3">
              <EmptyState icon={Calendar} title="No upcoming appointments" description={isProvider ? "Nothing scheduled across the practice" : "Book an appointment with your care team"} />
              {!isProvider && (
                <div className="bg-accent/50 border border-border rounded-xl p-4 space-y-2" data-testid="appointments-how-it-works">
                  <p className="text-sm font-semibold">How appointments work</p>
                  <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1 leading-relaxed">
                    <li>Appointments booked in IntakeQ appear here automatically.</li>
                    <li>Tap any appointment to see telehealth links, add it to your calendar, or request a reschedule.</li>
                    <li>Reminders arrive 24 h and 2 h before each visit (if your phone number is on file).</li>
                  </ul>
                </div>
              )}
              {!isProvider && source === "local" && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-amber-900 text-sm mb-1">Missing appointments?</h3>
                    <p className="text-xs text-amber-800 mb-2">If you don't see your appointments, try reconnecting your IntakeQ account.</p>
                    <button onClick={() => setReconnectDialogOpen(true)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-900 text-amber-50 text-sm font-medium hover:bg-amber-800 transition-colors" data-testid="reconnect-intakeq-button">
                      Reconnect IntakeQ Account
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            filteredUpcoming.map((apt) => (
              isProvider ? (
                <ProviderAppointmentCard key={apt.id || apt.intakeq_id} appointment={apt} />
              ) : (
                <div key={apt.id || apt.intakeq_id} onClick={() => apt.source !== "intakeq" && setSelectedApt(apt)} className={apt.source !== "intakeq" ? "cursor-pointer" : ""}>
                  <AppointmentCard appointment={apt} />
                </div>
              )
            ))
          )}
        </TabsContent>
        <TabsContent value="past" className="space-y-3">
          {filteredPast.length === 0 ? (
            <EmptyState icon={Calendar} title="No past appointments" description="Your appointment history will appear here" />
          ) : (
            filteredPast.map((apt) => (
              isProvider ? (
                <ProviderAppointmentCard key={apt.id || apt.intakeq_id} appointment={apt} />
              ) : (
                <AppointmentCard key={apt.id || apt.intakeq_id} appointment={apt} />
              )
            ))
          )}
        </TabsContent>
      </Tabs>
      <BookAppointmentDialog open={bookDialogOpen} onOpenChange={setBookDialogOpen} onBooked={() => fetchAppointments(true)} />
      <IntakeQReconnectDialog open={reconnectDialogOpen} onOpenChange={setReconnectDialogOpen} onSuccess={() => fetchAppointments(true)} />
      <AppointmentActions appointment={selectedApt} open={!!selectedApt} onClose={() => setSelectedApt(null)} onUpdated={() => { setSelectedApt(null); fetchAppointments(); }} />
    </div>
  );
}
